import { Transition } from "@headlessui/react";
import { useMetaTags } from "@launerlondon/shop-hooks";
import { useLayoutEffect, useState } from "react";
import { ParallaxBanner } from "react-scroll-parallax";
import { Link } from "react-router";
import { routes } from "@launerlondon/shop-router";

const Customise: React.FC = () => {
	useMetaTags({ title: "Terms and conditions" });
	const bannerImages = ["/images/pages/customise-banner.jpg"];
	const [imageBannerIndex, setImageBannerIndex] = useState(0);

	useLayoutEffect(() => {
		const interval = setInterval(
			() => setImageBannerIndex((p) => (p + 1) % 3),
			4e3,
		);
		return () => clearInterval(interval);
	}, []);

	return (
		<div>
			<ParallaxBanner
				layers={[
					{
						speed: -20,
						expanded: false,
						children: (
							<div>
								{bannerImages.map((i, idx) => (
									<Transition
										key={idx}
										show={imageBannerIndex === idx}
										enterFrom="opacity-0"
										leaveTo="opacity-0"
										className="ln-banner-image"
										style={{ backgroundImage: `url(${i}` }}
									/>
								))}
							</div>
						),
					},
				]}
				className="ln-banner"
			/>
			<article className="container" id="terms-and-conditions">
				<header className="ln-intro-grid">
					<h2 className="font-display">Terms and Conditions</h2>
					<div>
						<p>
							The Terms and Conditions outlined below apply to all
							transactions on this site. Please read them
							carefully. They do not affect your statutory rights.
						</p>
					</div>
				</header>
				<div className="ln-section-grid gap-10">
					<div>
						<p>
							We may change these terms and conditions at any
							time. Any changes will take effect on the date they
							are posted onto the site.
						</p>
						<ul className="list-disc space-y-2 pl-4">
							<li>
								All items shown are subject to availability.
							</li>
							<li>
								An email confirmation of your order will be sent
								to the email address you specified when placing
								your order. Please check that all details
								accurately reflect your requirements. Should
								there be any discrepancies, please email us on
								the contact us page.
							</li>
							<li>
								Where an order requests several different items
								to be sent to the same address, then these may
								be dispatched separately and therefore we cannot
								guarantee delivery on the same day.
							</li>
							<li>
								All our goods are guaranteed against
								manufacturing faults only and not excessive wear
								and tear or accidental damage made by the user.
							</li>
							<li>
								In the event of non-arrival please contact the
								Customer Service Department on 01883 625562 and
								quote the reference number shown on the receipt.
							</li>
							<li>
								For items to be exported, the carriage charge
								covers packaging, delivery and insurance only.
							</li>
							<li>
								Customs Duties may be payable by the overseas
								recipient and we regret that we are unable to
								advise of these amounts.
							</li>
							<li>
								This policy does not affect your statutory
								rights.
							</li>
							<li>
								The website is controlled and operated in the
								UK. These Terms and Conditions and all matters
								connected with any order you place on our
								website are governed by English law and you
								agree to submit to the exclusive jurisdiction of
								the English courts in relation to all matters
								connected with or arising out of the website or
								any order you place on the website.
							</li>
						</ul>
					</div>
				</div>
			</article>
			<article
				className="ln-section-grid container mt-10 gap-10 border-t py-10"
				id="vouchers-gift-cards"
			>
				<h2 className="font-display text-3xl font-light">
					E-vouchers and gift-cards
				</h2>
				<div className="flex flex-col gap-4 divide-y">
					<div className="space-y-4">
						<h3 className="ln-subtitle">
							Additional information on how it works.
						</h3>
						<ul className="list-disc space-y-2 pl-4">
							<li>
								Valid for 12 months from the date of purchase,
								this is tracked in the user account area and
								reminders will be sent to the original purchaser
								via email prior to expiration.
							</li>
							<li>
								Transactions and any balance can be viewed by
								logging in to the user account area.
							</li>
							<li>Redeemable only at Launer.com (UK).</li>
							<li>
								The e-voucher is available in Pound Sterling, US
								dollar and Euro. This is subject to live
								exchange rates in operation at the point of gift
								voucher purchase.
							</li>
							<li>
								The e-voucher is not transferable,
								non-refundable, non-returnable and cannot be
								exchanged for a cash alternative.
							</li>
							<li>
								Should a purchase made via e-voucher be subject
								to a fault, the item will be repaired or remade.
							</li>
							<li>
								Multiple e-vouchers can be used within a single
								purchase
							</li>
							<li>
								E-vouchers can be used as part payment against a
								total balance or, If the total at checkout is
								less than that of the voucher being used then
								any remaining balance will remain stored for
								future redemption.{" "}
							</li>
							<li>
								For queries and support please contact{" "}
								<Link
									to={routes.contact}
									className="text-accent underline"
								>
									Customer Services
								</Link>
							</li>
						</ul>
					</div>
				</div>
			</article>
			<article
				className="ln-section-grid container mt-10 gap-10 border-t py-10"
				id="100-gift-offer"
			>
				<h2 className="font-display text-3xl font-light">
					Launer London £100 gift offer
				</h2>
				<div className="flex flex-col gap-4 divide-y">
					<div className="space-y-4">
						<h3 className="ln-subtitle">
							Additional information on how it works.
						</h3>
						<ul className="list-disc space-y-2 pl-4">
							<li>
								The terms and conditions apply to the online
								action ‘LAUNER1OO' AND 'ONEHUNDRED’. The
								promotion runs from 10th June to July 31st 2025.
							</li>
							<li>
								The promoter is: Launer London Limited
								registered in England and Wales no 01582057
								whose registered office is: Classic House, 365A
								Limpsfield Road, Warlingham, Surrey CR6 9HA
								United Kingdom.
							</li>
							<li>
								The offer is open to residents of the United
								Kingdom and Worldwide aged 18 years or over.
								This excludes employees of Launer London Ltd and
								its immediate families, its agents or anyone
								professionally connected with the competition.
							</li>
							<li>
								The promotion provides £100 or the equivalent in
								other accepted currencies* deducted from the
								full price of any Launer handbag via the
								Launer.com website in the form of a code applied
								at check out.
							</li>
							<li>
								You can use the same promotion code multiple
								times to receive the £100 gift off the full
								purchase price of a Launer handbag ,but only
								once per transaction.
							</li>
							<li>
								By signing up to the Launer newsletter, you
								agree to receive marketing emails from Launer
								London Ltd. Launer does not provide customer
								information to third parties. For more
								information please read our PRIVACY POLICY. You
								can unsubscribe at any time.
							</li>
							<li>
								There is no cash alternative or product
								substitution. The discount is not transferable
								and must be spent in one purchase.
							</li>
							<li>
								Eligibility for the promotion is as follows: you
								need to be an existing subscriber to the Launer
								newsletter or sign up to receive the Launer
								newsletter.
							</li>
							<li>
								By entering the promotion, entrants confirm that
								they have read and agree to be bound by these
								terms and conditions and by the decision of the
								promoter, which is final in all matters relating
								to the promotion. Failure to do so will result
								in forfeiture of the promotion gift. No
								correspondence will be entered into.
							</li>
							<li>
								The promoter’s website can be found at :
								www.Launer.com.
							</li>
							<li>
								This promotions not endorsed, sponsored or
								administered in anyway by Meta where the
								promotion may appear in addition to the Launer
								website and newsletter. Please do not respond to
								any other profile which appear to imitate our
								page or click links you may receive.
							</li>
							<li>
								Any information submitted by you must be
								personal to and relate specifically to you. You
								warrant that your entry and all information
								which you submit and/or distribute will not
								infringe the intellectual property, privacy or
								any other rights of any third party, and will
								not contain anything which is libellous,
								defamatory, obscene, indecent, harassing or
								threatening. In addition, you warrant that you
								have permission to use or feature any people,
								content or other material contained within your
								entry. The Promoter reserves the right to
								screen, filter and/or monitor entries for this
								purpose.
							</li>
							<li>
								The Promoter or its agencies will not be
								responsible for the non-inclusion of entries as
								a result of technical failures or otherwise,
								including any such failure which is within the
								control of The Promoter or its agencies. Proof
								of submission of entry is not proof of receipt
								of entry.
							</li>
							<li>
								The Promoter accepts no responsibility for any
								third party illegal or fraudulent
								action/inaction that may occur on or through the
								promotion but reserves the right to take action
								against any third party it knows or has
								reasonable grounds to believe is responsible for
								such action/inaction. The Promoter reserves the
								right at its sole discretion to disqualify any
								person it finds to be tampering or to have
								tampered with the operation of the promotion or
								the Promoter promotional website, or to be
								acting in violation of these terms and
								conditions.
							</li>
							<li>
								The Promoter will not be liable for any loss
								(including, without limitation, indirect,
								special or consequential loss), expense OR
								damage, injury or death which is suffered or
								sustained (whether or not arising from any
								person's negligence) in connection with use of
								the promotion.
							</li>
							<li>
								The Promotional website may contain links to
								other websites that the Promoter does not own or
								operate. Please be aware that the Promoter does
								not control, recommend or endorse, and is not
								responsible for their content, products,
								services or privacy policies.
							</li>
							<li>
								Whilst we will always try to ensure that the
								promotional website is working as it should be,
								problems may occur from time to time. The
								promoter accepts no responsibility for any
								intermittent problems, which may arise.
							</li>
							<li>
								If for any reason any aspect of the Promoter is
								not capable of operating as intended, including
								by reason of infection by computer virus,
								network failure, bugs, tampering, unauthorised
								intervention, fraud, technical failures or any
								cause beyond the control of the Promoter which
								corrupts or affects the administration,
								security, fairness, integrity or proper conduct
								of this offer, the Promoter may at its sole
								discretion cancel, terminate, modify or suspend
								the promotion.
							</li>
							<li>
								No failure to enforce a clause shall be deemed a
								waiver of the rights covered within it.
							</li>
							<li>
								If any clause of these Terms is deemed
								unenforceable, then it shall be severed, and the
								remaining Terms shall remain in force.
							</li>
							<li>
								These terms and conditions are subject to
								English law and the exclusive jurisdiction of
								the English Courts. The Promoter reserves the
								rights, at its sole discretion, to modify these
								Terms at any time, and limits its liability in
								relation to such action to the fullest extent
								permissible under English law.
							</li>
						</ul>
					</div>
				</div>
			</article>
			<article
				className="ln-section-grid container mt-10 gap-10 border-t py-10"
				id="privacy-and-cookies-policy"
			>
				<h2 className="font-display text-3xl font-light">
					Privacy Policy and Cookies
				</h2>
				<div className="flex flex-col gap-4 divide-y">
					<div className="space-y-4">
						<h3 className="ln-subtitle">
							What do we use your information for?
						</h3>
						<p>
							Any of the information we collect from you may be
							used in one of the following ways:
						</p>
						<ul className="list-disc space-y-2 pl-4">
							<li>
								To personalise your experience (your information
								helps us to better respond to your individual
								needs)
							</li>
							<li>
								To improve our website (we continually strive to
								improve our website offerings based on the
								information and feedback we receive from you)
							</li>
							<li>
								To improve customer service (your information
								helps us to more effectively respond to your
								customer service requests and support needs)
							</li>
							<li>To process transactions</li>
							<li>
								Your information, whether public or private,
								will not be sold, exchanged, transferred, or
								given to any other company for any reason
								whatsoever, without your consent, other than for
								the express purpose of delivering the purchased
								product or service requested.
							</li>
							<li>
								To send periodic emails (if you have previously
								opted in to receive these)
							</li>
						</ul>
						<p>
							Note: If at any time you would like to unsubscribe
							from receiving future emails, we include detailed
							unsubscribe instructions at the bottom of each
							emailed newsletter.
						</p>
					</div>
					<div className="space-y-4 pt-10">
						<h3 className="ln-subtitle">
							How do we protect your information?
						</h3>
						<p>
							We implement a variety of security measures to
							maintain the safety of your personal information
							when you place an order or enter, submit, or access
							your personal information.
						</p>
						<p>
							We offer the use of a secure server. All supplied
							sensitive information is transmitted via Secure
							Socket Layer (SSL) technology and then encrypted
							into our Payment gateway providers database only to
							be accessible by those authorized with special
							access rights to such systems, and are required to
							keep the information confidential.
						</p>
					</div>
					<div className="space-y-4 pt-10">
						<h3 className="ln-subtitle">Do we use cookies?</h3>
						<p>
							Yes (Cookies are small files that a site or its
							service provider transfers to your computer's hard
							drive through your Web browser (if you allow) that
							enables the site's or service provider's systems to
							recognise your browser and capture and remember
							certain information).
						</p>

						<p>
							We use cookies to help us remember and process the
							items in your shopping cart, understand and save
							your preferences for future visits and keep track of
							advertisements.
						</p>
					</div>
					<div className="space-y-4 pt-10">
						<h3 className="ln-subtitle">What cookies do we use?</h3>
						<dl className="space-y-4">
							<dt className="font-medium"> _gid </dt>
							<dd>
								This cookie is installed by Google Analytics.
								The cookie is used to store information of how
								visitors use a website and helps in creating an
								analytics report of how the website is doing.{" "}
							</dd>
							<dt className="font-medium">__cf_bm</dt>
							<dd>
								<p>
									Cloudflare's bot products identify and
									mitigate automated traffic to protect your
									site from bad bots. Cloudflare places the
									__cf_bm cookie on End User devices that
									access Customer sites that are protected by
									Bot Management or Bot Fight Mode. The
									__cf_bm cookie is necessary for the proper
									functioning of these bot solutions.
								</p>
								<p>
									This cookie expires after (at most) 30
									minutes of continuous inactivity by the End
									User. The cookie contains information
									related to the calculation of Cloudflare’s
									proprietary bot score and, when Anomaly
									Detection is enabled on Bot Management, a
									session identifier. The information in the
									cookie (other than time-related information)
									is encrypted and can only be decrypted by
									Cloudflare.
								</p>
								<p>
									A separate __cf_bm cookie is generated for
									each site that an End User visits, and
									Cloudflare does not follow users from site
									to site or from session to session by
									merging various __cf_bm identifiers into a
									profile. The __cf_bm cookie is generated
									independently by Cloudflare, and does not
									correspond to any user ID or other
									identifiers in a Customer’s web application.
								</p>
							</dd>
							<dt className="font-medium">_gat_gtag</dt>
							<dd>
								<p>
									the gtag.js JavaScript library uses
									first-party cookies to:
								</p>
								<ul className="mt-2 list-disc pl-4">
									<li>Distinguish unique users</li>
									<li>Distinguish sessions for a user</li>
								</ul>
							</dd>
							<dt className="font-medium">_ga</dt>
							<dd>
								<p>
									The ga.js JavaScript library uses
									first-party cookies to:
								</p>
								<ul className="mt-2 list-disc pl-4">
									<li>Determine which domain to measure</li>
									<li>Distinguish unique users</li>
									<li>Throttle the request rate</li>
									<li>
										Remember the number and time of previous
										visits
									</li>
									<li>Remember traffic source information</li>
									<li>
										Determine the start and end of a session
									</li>
									<li>
										Remember the value of visitor-level
										custom variables
									</li>
								</ul>
							</dd>
						</dl>
					</div>
					<div className="space-y-4 pt-10">
						<h3 className="ln-subtitle">
							Do we disclose any information to outside parties?
						</h3>
						<p>
							We do not sell, trade, or otherwise transfer to
							outside parties your personally identifiable
							information. This does not include trusted third
							parties who assist us in operating our website,
							conducting our business, or servicing you, so long
							as those parties agree to keep this information
							confidential. We may also release your information
							when we believe release is appropriate to comply
							with the law, enforce our site policies, or protect
							ours or others' rights, property, or safety.
							However, non-personally identifiable visitor
							information may be provided to other parties for
							marketing, advertising, or other uses.
						</p>
					</div>
					<div className="space-y-4 pt-10">
						<h3 className="ln-subtitle">
							Online Privacy Policy Only
						</h3>
						<p>
							This online privacy policy applies only to
							information collected through our website and not to
							information collected offline.
						</p>
					</div>
					<div className="space-y-4 pt-10">
						<h3 className="ln-subtitle">Your Consent</h3>
						<p>
							By using our site, you consent to our privacy
							policy.
						</p>
					</div>
					<div className="space-y-4 pt-10">
						<h3 className="ln-subtitle">
							Changes to our Privacy Policy
						</h3>
						<p>
							If we decide to change our privacy policy, we will
							post those changes on this page, and/or update the
							Privacy Policy modification date below.
						</p>
					</div>
				</div>
			</article>
		</div>
	);
};

export default Customise;
